import { createFileRoute } from "@tanstack/react-router";
import { formatDistanceToNowStrict, parseISO } from "date-fns";
import { ErrorState } from "@/components/brand/error-state";
import { PageHeader } from "@/components/layout/page-header";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ErrorLog, HealthBoard, useSystemHealth } from "@/features/system";

/**
 * Is the platform running, and has anything fallen behind?
 *
 * The one screen to open when a client says "my numbers have not moved" or
 * "my post did not go out". Every job's last run, every worker's heartbeat,
 * and every piece of work that is past due, in words a person can act on.
 */
export const Route = createFileRoute("/_authenticated/admin/system")({
  component: SystemHealthPage,
});

const VERDICT = {
  ok: { label: "All good", variant: "success" as const },
  attention: { label: "Needs attention", variant: "warning" as const },
  down: { label: "Something is down", variant: "destructive" as const },
};

function SystemHealthPage() {
  const { data, isPending, isError, refetch, dataUpdatedAt } = useSystemHealth();

  return (
    <div>
      <PageHeader
        eyebrow="Platform"
        title="System health"
        description="The worker, the scheduled jobs, and any work that is behind. Refreshes every minute."
        actions={
          data ? (
            <span className="flex items-center gap-2">
              <Badge variant={VERDICT[data.verdict].variant}>{VERDICT[data.verdict].label}</Badge>
              <span className="text-muted-foreground text-xs">
                Checked {formatDistanceToNowStrict(parseISO(data.generatedAt))} ago · v
                {data.version}
              </span>
            </span>
          ) : null
        }
      />
      {isPending ? (
        <Skeleton className="h-96 rounded-lg" />
      ) : isError ? (
        <ErrorState onRetry={() => void refetch()} />
      ) : (
        <HealthBoard key={dataUpdatedAt} health={data} />
      )}
      <div className="mt-8">
        <ErrorLog />
      </div>
    </div>
  );
}
